import { datadogRum } from '@datadog/browser-rum';
import type { Store } from 'redux';

import type { ReduxState } from '../../../redux';

export { DatadogRouterTracker } from './DatadogRouterTracker';

export const DATADOG_SERVICE = 'onboarding';

// Hostnames whose resource/activity events add no diagnostic value but inflate RUM noise.
// Add new analytics/observability vendors here as they're integrated.
const ANALYTICS_HOST_PATTERNS = [
    /(?:^|\/\/|\.)segment\.(io|com)\b/,
    /(?:^|\/\/|\.)amplitude\.com\b/,
    /(?:^|\/\/|\.)clarity\.ms\b/,
    /(?:^|\/\/|\.)plotline\.so\b/,
    /(?:^|\/\/|\.)tidio(?:chat)?\.(co|com)\b/,
    /(?:^|\/\/|\.)googletagmanager\.com\b/,
    /(?:^|\/\/|\.)google-analytics\.com\b/,
    /(?:^|\/\/|\.)freshchat\.com\b/
];

const isAnalyticsCall = (url?: unknown): boolean => {
    if (typeof url !== 'string') return false;
    return ANALYTICS_HOST_PATTERNS.some((re) => re.test(url));
};

// Error messages that are environmental noise (network/storage races) rather than actionable bugs.
const IGNORED_ERROR_PATTERNS = ['remoteconfig/fetch-client-network', 'remoteconfig/storage-open'];

const isIgnoredError = (message?: unknown): boolean => {
    if (typeof message !== 'string') return false;
    return IGNORED_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
};

const normalizeEnv = (raw: string | undefined): string => {
    switch (raw) {
    case 'development':
        return 'dev';
    case 'performance':
        return 'perf';
    case 'production':
        return 'prod';
    default:
        return raw ?? 'unknown';
    }
};

export const captureError = (error: unknown, context?: Record<string, unknown>): void => {
    datadogRum.addError(error, context);
};

export const initializeDatadog = (store: Store<ReduxState>): void => {
    try {
        const env = process.env.REACT_APP_ENV;
        const applicationId = process.env.REACT_APP_DATADOG_APPLICATION_ID;
        const clientToken = process.env.REACT_APP_DATADOG_CLIENT_TOKEN;
        const site = process.env.REACT_APP_DATADOG_SITE || 'datadoghq.eu';
        const version = process.env.REACT_APP_VERSION;
        const sessionSampleRate = Number(process.env.REACT_APP_DATADOG_SESSION_SAMPLE_RATE) || 100;
        const sessionReplaySampleRate = Number(process.env.REACT_APP_DATADOG_SESSION_REPLAY_SAMPLE_RATE) || 20;

        if (!!applicationId && !!clientToken) {
            datadogRum.init({
                applicationId,
                clientToken,
                site,
                service: DATADOG_SERVICE,
                env: normalizeEnv(env),
                version,
                sessionSampleRate,
                sessionReplaySampleRate,
                defaultPrivacyLevel: 'mask-user-input',
                trackUserInteractions: true,
                trackResources: true,
                trackViewsManually: true,
                trackLongTasks: true,
                // Persist the RUM session (_dd_s cookie) across *.loophealth.com so cross-subdomain
                // redirects (onboarding <-> benefits) don't fragment one visit into many billed sessions.
                trackSessionAcrossSubdomains: true,
                useSecureSessionCookie: true,
                allowedTracingUrls: [/https:\/\/api.*\.loophealth\.com/, /https:\/\/api\.loophealth\.com/],
                // Label click actions by semantic attribute instead of styled-components hash classes.
                // Datadog supports a single custom attribute; data-track is our primary marker and DD's
                // own heuristics (aria-label/text/id) cover the rest.
                actionNameAttribute: 'data-track',
                // Keep analytics-vendor calls out of loading/activity timing so they don't skew metrics.
                excludedActivityUrls: ANALYTICS_HOST_PATTERNS,
                beforeSend: (event) => {
                    // Drop analytics-vendor resource events — diagnostic noise.
                    if (event.type === 'resource' && isAnalyticsCall(event.resource?.url)) return false;
                    // Drop environmental error noise (remote-config network/storage races).
                    if (event.type === 'error' && isIgnoredError(event.error?.message)) return false;
                    return true;
                }
            });
        }

        // Keep datadogRum.setUser in sync with the redux store so all events (errors,
        // actions, views) carry user identity automatically, not just captured errors.
        let lastUserId: string | undefined;
        store.subscribe(() => {
            const user = store.getState().user?.userData?.data;
            if (user?.userId === lastUserId) return;
            lastUserId = user?.userId;
            if (user?.userId) {
                datadogRum.setUser({
                    id: user.userId,
                    email: user.email,
                    employer: user.employer,
                    mobile: user.mobile
                });
            } else {
                datadogRum.clearUser();
            }
        });
    } catch {
        // Silently catch initialization errors to prevent app crashes
    }
};
